import type { NavigateFunction } from 'react-router-dom';
import { supabaseAuth } from '../services/supabaseAuth';

interface StoredUser {
  id?: string;
  isGuest?: boolean;
}

interface AuthSessionOptions {
  maxAttempts?: number;
  delayMs?: number;
}

function getStoredUser(): StoredUser | null {
  const raw = localStorage.getItem('currentUser');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

async function wait(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export async function hasAuthenticatedSession(options: AuthSessionOptions = {}): Promise<boolean> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
  const delayMs = Math.max(0, options.delayMs ?? 250);

  let token: string | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    token = await supabaseAuth.getAccessToken();
    if (token) break;
    if (attempt < maxAttempts) {
      await wait(delayMs);
    }
  }

  if (!token) return false;

  const user = getStoredUser();
  if (user?.id && !user.isGuest) return true;

  let supabaseUser = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    supabaseUser = await supabaseAuth.getCurrentUser();
    if (supabaseUser?.id) break;
    if (attempt < maxAttempts) {
      await wait(delayMs);
    }
  }

  if (!supabaseUser?.id) return false;

  const normalizedUser = {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.name || 'User',
    createdAt: new Date().toISOString(),
    isGuest: false,
  };
  localStorage.setItem('currentUser', JSON.stringify(normalizedUser));
  localStorage.setItem('userAuthenticated', 'true');
  localStorage.setItem('userId', normalizedUser.id);
  localStorage.setItem('userEmail', normalizedUser.email);
  localStorage.setItem('username', normalizedUser.name);
  return true;
}

export async function ensureAuthBeforeCheckout(
  navigate: NavigateFunction,
  returnTo = '/subscription'
): Promise<boolean> {
  const authed = await hasAuthenticatedSession();
  if (authed) return true;
  navigate('/signup', { state: { returnTo } });
  return false;
}
